// Overview tab: headline numbers, money and status tables per PM, and the All jobs table.
import type { Item, Job } from '@shared/index';
import { STATUSES, STATUS_LABEL, SOON_DAYS, compact, craneFlag, fmtDMY, money, sum } from '@shared/index';
import { activePms, jobItems, store, today, ui } from '../store';
import { registerView, render } from '../render';
import { openJob, setPm } from '../nav';
import { $, el, pmSeg, segBind, td } from '../ui';

export interface PmRow {
  pm: string; R: Job[]; jobs: number; jv: number; vars: number; vv: number;
  total: number; inv: number; rem: number; pct: number;
}

/** One money row per PM. Jobs whose PM isn't in `pms` only show in the team totals. */
export function perPm(all: Job[], pms: string[]): PmRow[] {
  return pms.map((pm) => {
    const R = all.filter((j) => j.pm === pm);
    const J = R.filter((j) => j.type !== 'Variation'), V = R.filter((j) => j.type === 'Variation');
    const total = sum(R, 'quoted'), rem = sum(R, 'remaining');
    return { pm, R, jobs: J.length, jv: sum(J, 'quoted'), vars: V.length, vv: sum(V, 'quoted'), total, inv: sum(R, 'invoiced'), rem, pct: total ? rem / total : 0 };
  });
}

const pctOf = (part: number, whole: number) => Math.round((whole ? part / whole : 0) * 100) + '%';

export interface AllJobsFilter { pm: string; ty: string; q: string; k: string; dir: number }

const NUM_KEYS = ['quoted', 'invoiced', 'remaining'];

function sortValue(j: Job, k: string): string | number {
  if (k === 'status') return STATUS_LABEL[j.status || 'not-started'] || '';
  if (NUM_KEYS.includes(k)) return Number((j as unknown as Record<string, unknown>)[k] || 0);
  if (k === 'date') return j.quote_date || '';
  return String((j as unknown as Record<string, unknown>)[k] || '');
}

/** The All jobs rows: filtered, then sorted with the newest quote date breaking ties. */
export function allJobsRows(jobs: Job[], f: AllJobsFilter): Job[] {
  const q = f.q.trim().toLowerCase();
  const R = jobs.filter((j) => (!f.pm || j.pm === f.pm) && (!f.ty || j.type === f.ty) &&
    (!q || (j.quote + ' ' + j.address + ' ' + j.builder).toLowerCase().includes(q)));
  return R.sort((a, b) => {
    const x = sortValue(a, f.k), y = sortValue(b, f.k);
    return ((x < y ? -1 : x > y ? 1 : 0) * f.dir) || ((a.quote_date || '') < (b.quote_date || '') ? 1 : -1);
  });
}

/** Sorting by a column: same column flips direction; a new one starts descending for numbers/dates, ascending for text. */
export function nextSort(cur: { k: string; dir: number }, k: string): { k: string; dir: number } {
  const num = [...NUM_KEYS, 'date'].includes(k);
  return { k, dir: cur.k === k ? -cur.dir : (num ? -1 : 1) };
}

function renderOverview() {
  const lede = $('lede');
  const all = store.jobs;
  if (!store.loaded) { lede.textContent = 'Loading…'; return; }
  if (!all.length) { lede.textContent = 'No jobs loaded yet.'; return; }
  const per = perPm(all, activePms());
  const T = { total: sum(all, 'quoted'), inv: sum(all, 'invoiced'), rem: sum(all, 'remaining') };
  const now = today();
  const cranes = jobItems().filter((j) => craneFlag(j, now)).length;
  lede.textContent = '';
  if (per.length) {
    const top = per.reduce((a, b) => (b.rem > a.rem ? b : a));
    lede.append(el('b', '', top.pm), ' has the most left to claim at ', el('b', '', compact(top.rem)), ', with ',
      el('b', '', Math.round(top.pct * 100) + '%'), ' of their work still to invoice.');
  }
  if (cranes) {
    const w = el('b', '', ' ' + cranes + (cranes === 1 ? ' job installs' : ' jobs install') + ' within ' + SOON_DAYS + ' days with no crane booked.');
    w.style.color = 'var(--bad)';
    lede.append(w);
  }
  $('t-q').textContent = compact(T.total); $('t-r').textContent = compact(T.rem); $('t-i').textContent = compact(T.inv);
  $('t-n').textContent = String(all.length);
  $('t-nh').textContent = all.filter((j) => j.type !== 'Variation').length + ' jobs + ' + all.filter((j) => j.type === 'Variation').length + ' variations';

  const max = Math.max(...per.map((p) => p.total), 1);
  const bars = $('bars'); bars.replaceChildren();
  per.forEach((p) => {
    const row = el('div', 'brow');
    const tr = el('div', 'btrack'); tr.style.width = (p.total / max * 100) + '%';
    const o = el('div', 'out'); o.style.width = (p.pct * 100) + '%';
    const i = el('div', 'inv'); i.style.flex = '1';
    tr.append(o, i);
    const cap = el('div', 'cap', compact(p.rem) + ' left (' + Math.round(p.pct * 100) + '%) of ' + compact(p.total) + ' · ' + compact(p.inv) + ' invoiced');
    row.append(el('div', 'nm', p.pm), tr, cap); bars.append(row);
  });

  const mb = $('money'); mb.replaceChildren();
  per.forEach((p) => {
    const r = el('tr');
    r.append(td(p.pm), td(p.jobs), td(money(p.jv)), td(p.vars), td(money(p.vv)), td(money(p.total)), td(money(p.inv)), td(money(p.rem)), td(Math.round(p.pct * 100) + '%'));
    mb.append(r);
  });
  const mf = $('money-f'); mf.replaceChildren();
  const fr = el('tr');
  const AJ = all.filter((j) => j.type !== 'Variation'), AV = all.filter((j) => j.type === 'Variation');
  fr.append(td('Team'), td(AJ.length), td(money(sum(AJ, 'quoted'))), td(AV.length),
    td(money(sum(AV, 'quoted'))), td(money(T.total)), td(money(T.inv)), td(money(T.rem)), td(pctOf(T.rem, T.total)));
  mf.append(fr);

  const keys = STATUSES.map(([k]) => k);
  const sb = $('stat'); sb.replaceChildren();
  per.forEach((p) => {
    const r = el('tr');
    const nameCell = el('td'); const a = el('a', '', p.pm); a.href = '#jobs';
    a.addEventListener('click', () => { setPm(p.pm); }); nameCell.append(a); r.append(nameCell);
    keys.forEach((k) => { const n = p.R.filter((j) => (j.status || 'not-started') === k).length; r.append(td(n, n ? '' : 'z')); });
    const c = (p.R as unknown as Item[]).filter((j) => craneFlag(j, now)).length; r.append(td(c, c ? 'hot' : 'z'));
    sb.append(r);
  });
  const sf = $('stat-f'); sf.replaceChildren();
  const sr = el('tr'); sr.append(td('Team'));
  keys.forEach((k) => sr.append(td(all.filter((j) => (j.status || 'not-started') === k).length)));
  const totC = (all as unknown as Item[]).filter((j) => craneFlag(j, now)).length;
  sr.append(td(totC, totC ? 'hot' : '')); sf.append(sr);
}

function renderAllJobs() {
  pmSeg($('ovPm'), ui.ov.pm, (pm) => { ui.ov.pm = pm; render(['alljobs']); });
  if (!store.loaded) return;
  const R = allJobsRows(store.jobs, ui.ov);
  const sumLine = $('ovSum'); sumLine.replaceChildren();
  const parts: [string, string?][] = [[R.length + ' quotes'], [' · quoted ', money(sum(R, 'quoted'))], [' · invoiced ', money(sum(R, 'invoiced'))], [' · remaining ', money(sum(R, 'remaining'))]];
  parts.forEach(([a, b]) => { if (b === undefined) sumLine.append(el('b', '', a)); else sumLine.append(a, el('b', '', b)); });
  const tb = $('ovRows'); tb.replaceChildren();
  R.forEach((j) => {
    const tr = el('tr');
    const qc = el('td', 'l'); const a = el('a', '', j.quote); a.href = '#jobs';
    a.addEventListener('click', (e) => { e.preventDefault(); openJob(j.quote); }); qc.append(a);
    tr.append(td(fmtDMY(j.quote_date), 'l'), qc, td(j.pm, 'l'), td(j.type, 'l' + (j.type === 'Variation' ? ' var' : '')), td(j.address, 'l addr-c'), td(j.builder, 'l'),
      td(STATUS_LABEL[j.status || 'not-started'] || '', 'l'), td(money(j.quoted)), td(Number(j.invoiced) ? money(j.invoiced) : '—'), td(money(j.remaining)));
    tb.append(tr);
  });
}

export function initOverview() {
  segBind('ovTy', 'ty', (v) => { ui.ov.ty = v; render(['alljobs']); });
  $<HTMLInputElement>('ovQ').addEventListener('input', (e) => { ui.ov.q = (e.target as HTMLInputElement).value; render(['alljobs']); });
  document.querySelectorAll<HTMLButtonElement>('.alljobs .srt').forEach((b) => b.addEventListener('click', () => {
    const k = b.dataset.k || '';
    Object.assign(ui.ov, nextSort(ui.ov, k));
    document.querySelectorAll<HTMLElement>('.alljobs .srt').forEach((x) => x.dataset.k === k ? x.setAttribute('aria-sort', ui.ov.dir < 0 ? 'descending' : 'ascending') : x.removeAttribute('aria-sort'));
    render(['alljobs']);
  }));
  registerView('overview', renderOverview);
  registerView('alljobs', renderAllJobs);
}
